<template>
  <div class="chatbot-input p-4 border-t border-gray-200 bg-white transition-all duration-300">
    <!-- Input Field, Mode Toggle, and Send Button -->
    <div class="flex items-center">
      
      <div class="flex-1 mr-2">
        <input
          ref="messageInput"
          v-model="messageText"
          placeholder="Type your message here..."
          class="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          @keydown.enter.prevent="sendMessage"
          :disabled="disabled"
        />
      </div>
            <!-- Mode Toggle -->
            <div class="relative inline-flex items-center mr-3">
        <button
          @click="toggleMode"
          class="px-2 py-1 text-sm font-medium rounded-md transition-all duration-300 flex items-center space-x-1 group"
          :class="isFastmodeActive ? 'text-blue-700' : 'text-gray-600'"
          :disabled="disabled"
        >
          <!-- Lightning icon for FastMode -->
          <svg v-if="isFastmodeActive" class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
          <!-- Brain icon for DeepMode -->
          <svg v-else class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
          </svg>
          
          <span class="relative">
            <span class="transition-opacity duration-300" :class="{'opacity-100': isFastmodeActive, 'opacity-0 absolute': !isFastmodeActive}">FastMode</span>
            <span class="transition-opacity duration-300" :class="{'opacity-100': !isFastmodeActive, 'opacity-0 absolute': isFastmodeActive}">DeepMode</span>
          </span>
        </button>
      
      </div>

      <button
        @click="sendMessage"
        class="p-3 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors flex-shrink-0 flex items-center justify-center"
        :disabled="!messageText.trim() || disabled"
        :class="{ 'opacity-50 cursor-not-allowed': !messageText.trim() || disabled }"
      >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 21L23 12L2 3L2 10L17 12L2 14L2 21Z" fill="currentColor"/>
      </svg>

      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';

// Local storage key for mode preference
const FASTMODE_STORAGE_KEY = 'chatbot_fastmode_active';

export default defineComponent({
  name: 'ChatbotInput',
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['send-message', 'mode-changed'],
  setup(props, { emit }) {
    const messageText = ref('');
    const messageInput = ref<HTMLInputElement | null>(null);
    
    // Initialize fastmode state from localStorage or default to true (FastMode)
    const isFastmodeActive = ref(localStorage.getItem(FASTMODE_STORAGE_KEY) !== 'false');
    
    // Function to toggle between FastMode and DeepMode
    const toggleMode = () => {
      isFastmodeActive.value = !isFastmodeActive.value;
      
      // Save preference to localStorage
      localStorage.setItem(FASTMODE_STORAGE_KEY, isFastmodeActive.value.toString());
      
      // Emit the mode change to parent component
      emit('mode-changed', isFastmodeActive.value);
    };
    
    // Function to send the message
    const sendMessage = () => {
      const trimmedMessage = messageText.value.trim();
      if (!trimmedMessage) return;
      
      // Emit the message to parent component with the current mode
      emit('send-message', trimmedMessage, isFastmodeActive.value);
      
      // Clear the input
      messageText.value = '';
      
      // Focus the input for next message
      if (messageInput.value) {
        messageInput.value.focus();
      }
    };
    
    // Set focus to input on mount and emit initial mode
    onMounted(() => {
      if (messageInput.value) {
        messageInput.value.focus();
      }
      
      // Emit the initial mode to parent component
      emit('mode-changed', isFastmodeActive.value);
    });
    
    return {
      messageText,
      messageInput,
      isFastmodeActive,
      toggleMode,
      sendMessage
    };
  }
});
</script>

<style scoped>
.chatbot-input input {
  height: 46px;
  transition: all 0.2s ease;
}

.chatbot-input input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  transform: translateY(-1px);
}

.chatbot-input button {
  transition: all 0.2s ease;
}

.chatbot-input button:hover:not(:disabled) {
  transform: scale(1.05);
}

.chatbot-input button:active:not(:disabled) {
  transform: scale(0.95);
}

/* Mode toggle button styles */
.chatbot-input button[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Position the absolute spans for the mode text */
.chatbot-input .relative .absolute {
  left: 0;
  top: 0;
}
</style>